import React, { useState, useRef, useEffect } from 'react';
import { getState, subscribe } from '../lib/userStore';
import AIAssistant from '../lib/components/AIAssistant';
import { lookupDrug, getSuggestions, getRxEstimate } from '../lib/costPlusDrugs';

const CARRIER_NAMES = {
  bcbs: 'BCBS', aetna: 'Aetna', cigna: 'Cigna',
  united: 'UnitedHealthcare', humana: 'Humana',
  medicaid: 'SC Medicaid', medicare: 'Medicare',
};
const PLAN_NAMES = { ppo: 'PPO', hmo: 'HMO', hdhp: 'HDHP', epo: 'EPO' };

const MED_STARTERS = [
  'What are the side effects of my medications?',
  'Can I take these medications together?',
  'Is my medication covered by my insurance?',
  'What is this medication used for?',
];

export default function MedicationsScreen({ navigate, goBack, medications, addMedication, removeMedication }) {
  const init = getState();
  const [carrier,  setCarrier]  = useState(init.carrier  || '');
  const [planType, setPlanType] = useState(init.planType || '');

  const [showAdd,      setShowAdd]      = useState(false);
  const [medName,      setMedName]      = useState('');
  const [dosage,       setDosage]       = useState('');
  const [frequency,    setFrequency]    = useState('');
  const [suggestions,  setSuggestions]  = useState([]);
  const [adding,       setAdding]       = useState(false);
  const [confirmId,    setConfirmId]    = useState(null); // id pending delete confirm
  const nameRef = useRef(null);

  useEffect(() => subscribe(s => {
    if (s.carrier  !== undefined) setCarrier(s.carrier   || '');
    if (s.planType !== undefined) setPlanType(s.planType || '');
  }), []);

  useEffect(() => {
    if (showAdd) setTimeout(() => nameRef.current?.focus(), 100);
  }, [showAdd]);

  function handleNameChange(val) {
    setMedName(val);
    setSuggestions(getSuggestions(val));
  }

  function pickSuggestion(drug) {
    setMedName(drug.name);
    setSuggestions([]);
    nameRef.current?.focus();
  }

  async function handleAdd() {
    if (!medName.trim() || adding) return;
    setAdding(true);
    const matched = lookupDrug(medName.trim());
    await addMedication({
      medication_name: medName.trim(),
      dosage:          dosage.trim()    || null,
      frequency:       frequency.trim() || null,
      is_generic:      matched ? true   : null,
      cost_plus_price: matched ? matched.price : null,
    });
    setMedName('');
    setDosage('');
    setFrequency('');
    setSuggestions([]);
    setShowAdd(false);
    setAdding(false);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') handleAdd();
    if (e.key === 'Escape') { setShowAdd(false); setSuggestions([]); }
  }

  const rxEstimate = getRxEstimate(carrier, planType);
  const carrierLabel = carrier ? (CARRIER_NAMES[carrier] || carrier) : null;
  const planLabel    = planType ? (PLAN_NAMES[planType]   || planType) : null;
  const planDisplay  = [carrierLabel, planLabel].filter(Boolean).join(' ') || null;

  return (
    <div style={s.screen}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.accentBar} />
        <button style={s.back} onClick={goBack}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 4L6 8l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back
        </button>
        <div style={s.title}>My <em style={{ fontStyle: 'italic', color: 'var(--teal)' }}>Medications</em></div>
        <div style={s.subtitle}>
          {medications.length > 0
            ? `${medications.length} medication${medications.length !== 1 ? 's' : ''} · compare costs`
            : 'Track meds and compare costs'}
        </div>
      </div>

      <div style={s.body}>
        <div style={s.bodyInner}>

          {/* Empty state */}
          {medications.length === 0 && !showAdd && (
            <div style={s.emptyCard}>
              <div style={s.emptyIcon}>💊</div>
              <div style={s.emptyTitle}>No medications yet</div>
              <div style={s.emptyText}>
                Add your medications to see cost comparisons between your insurance and Cost Plus Drugs — and get AI answers about your prescriptions.
              </div>
              <button style={s.emptyAddBtn} onClick={() => setShowAdd(true)}>
                Add your first medication
              </button>
            </div>
          )}

          {/* Medication cards */}
          {medications.map(med => {
            const costPlus = med.cost_plus_price;
            const isPending = confirmId === med.id;

            // Determine if Cost Plus is cheaper (only when we have both values)
            let costPlusCheaper = false;
            if (costPlus && rxEstimate && !rxEstimate.includes('deductible')) {
              const low = parseFloat(rxEstimate.replace(/[^0-9.]/g, ''));
              if (!isNaN(low)) costPlusCheaper = costPlus < low;
            }

            return (
              <div key={med.id} style={s.medCard}>
                {/* Card header */}
                <div style={s.medCardTop}>
                  <div style={s.medInfo}>
                    <div style={s.medName}>{med.medication_name}</div>
                    {(med.dosage || med.frequency) && (
                      <div style={s.medSub}>
                        {[med.dosage, med.frequency].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                  {isPending ? (
                    <div style={s.deleteConfirm}>
                      <span style={s.deleteConfirmText}>Remove?</span>
                      <button style={s.deleteYes} onClick={() => { removeMedication(med.id); setConfirmId(null); }}>Yes</button>
                      <button style={s.deleteNo}  onClick={() => setConfirmId(null)}>No</button>
                    </div>
                  ) : (
                    <button style={s.deleteBtn} onClick={() => setConfirmId(med.id)} aria-label="Remove medication">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </button>
                  )}
                </div>

                {/* Rx cost snapshot */}
                <div style={s.rxRow}>
                  {/* Insurance estimate */}
                  <div style={s.rxCol}>
                    <div style={s.rxLabel}>
                      {planDisplay ? `Your ${planDisplay}` : 'With insurance'}
                    </div>
                    <div style={s.rxValue}>
                      {rxEstimate || (carrier ? 'See plan' : 'Add insurance')}
                    </div>
                    {!carrier && (
                      <button style={s.rxLink} onClick={() => navigate('insurance')}>Set up →</button>
                    )}
                  </div>

                  <div style={s.rxDivider}>vs</div>

                  {/* Cost Plus */}
                  <div style={s.rxCol}>
                    <div style={s.rxLabel}>Cost Plus Drugs</div>
                    {costPlus ? (
                      <div style={{ ...s.rxValue, color: costPlusCheaper ? 'var(--teal)' : 'var(--ink)' }}>
                        ${costPlus.toFixed(2)}/mo
                        {costPlusCheaper && <span style={s.saveBadge}>Save</span>}
                      </div>
                    ) : (
                      <a
                        href={`https://costplusdrugs.com/medications/search/?q=${encodeURIComponent(med.medication_name)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={s.rxLink}
                      >
                        Check price →
                      </a>
                    )}
                  </div>
                </div>

                {/* Cost Plus savings callout */}
                {costPlus && costPlusCheaper && (
                  <div style={s.savingsCallout}>
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style={{ flexShrink: 0 }}>
                      <circle cx="6.5" cy="6.5" r="5.5" stroke="currentColor" strokeWidth="1.2"/>
                      <path d="M4 6.5l2 2 3-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Cost Plus may be cheaper than your insurance copay for this generic.
                    <a
                      href={`https://costplusdrugs.com/medications/search/?q=${encodeURIComponent(med.medication_name)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={s.savingsLink}
                    >
                      View at Cost Plus →
                    </a>
                  </div>
                )}
              </div>
            );
          })}

          {/* Add medication form */}
          {showAdd && (
            <div style={s.addCard}>
              <div style={s.addTitle}>Add medication</div>

              {/* Name field with suggestions */}
              <div style={s.addFieldGroup}>
                <div style={s.addFieldLabel}>Medication name</div>
                <input
                  ref={nameRef}
                  style={s.addInput}
                  value={medName}
                  onChange={e => handleNameChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="e.g. Metformin or Lisinopril"
                  autoComplete="off"
                />
                {suggestions.length > 0 && (
                  <div style={s.suggestions}>
                    {suggestions.map(drug => (
                      <button
                        key={drug.generic}
                        style={s.suggestionRow}
                        onMouseDown={e => { e.preventDefault(); pickSuggestion(drug); }}
                      >
                        <span style={s.suggestionName}>{drug.name}</span>
                        <span style={s.suggestionMeta}>{drug.category} · ${drug.price.toFixed(2)}/mo at Cost Plus</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div style={s.addRow}>
                <div style={{ ...s.addFieldGroup, flex: 1 }}>
                  <div style={s.addFieldLabel}>Dosage <span style={s.optional}>optional</span></div>
                  <input
                    style={s.addInput}
                    value={dosage}
                    onChange={e => setDosage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="e.g. 10mg"
                  />
                </div>
                <div style={{ ...s.addFieldGroup, flex: 1 }}>
                  <div style={s.addFieldLabel}>Frequency <span style={s.optional}>optional</span></div>
                  <input
                    style={s.addInput}
                    value={frequency}
                    onChange={e => setFrequency(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="e.g. once daily"
                  />
                </div>
              </div>

              <div style={s.addActions}>
                <button style={s.cancelBtn} onClick={() => { setShowAdd(false); setMedName(''); setDosage(''); setFrequency(''); setSuggestions([]); }}>
                  Cancel
                </button>
                <button
                  style={{ ...s.addBtn, opacity: (!medName.trim() || adding) ? 0.5 : 1 }}
                  onClick={handleAdd}
                  disabled={!medName.trim() || adding}
                >
                  {adding ? 'Adding…' : 'Add medication'}
                </button>
              </div>
            </div>
          )}

          {/* Add button — shown when list exists and form is closed */}
          {!showAdd && medications.length > 0 && (
            <button style={s.addMoreBtn} onClick={() => setShowAdd(true)}>
              + Add medication
            </button>
          )}

          {/* AI Assistant */}
          <div style={s.sectionLabel}>Ask about your medications</div>
          <AIAssistant
            placeholder="Ask about side effects, interactions, coverage…"
            starterPrompts={MED_STARTERS}
          />

          <div style={{ height: '8px' }} />
        </div>
      </div>
    </div>
  );
}

const s = {
  screen:   { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--cream)' },
  header:   { padding: '52px 24px 20px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', position: 'relative', flexShrink: 0 },
  accentBar:{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, var(--teal), var(--teal-2))' },
  back:     { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 500, color: 'var(--ink-3)', cursor: 'pointer', marginBottom: '16px', background: 'none', border: 'none', padding: 0, fontFamily: 'var(--font-body)' },
  title:    { fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: '26px', color: 'var(--ink)', letterSpacing: '-0.5px', lineHeight: 1.2 },
  subtitle: { fontSize: '13px', color: 'var(--ink-4)', marginTop: '4px' },
  body:     { flex: 1, minHeight: 0, overflowY: 'auto', msOverflowStyle: 'none', scrollbarWidth: 'none' },
  bodyInner:{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '20px' },
  sectionLabel: { fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-4)', marginBottom: '-4px' },

  // Empty state
  emptyCard:  { background: 'var(--surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '32px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '12px' },
  emptyIcon:  { fontSize: '36px' },
  emptyTitle: { fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 400, color: 'var(--ink)' },
  emptyText:  { fontSize: '14px', color: 'var(--ink-3)', lineHeight: 1.5, maxWidth: '280px' },
  emptyAddBtn:{ padding: '13px 28px', background: 'var(--teal)', color: 'white', border: 'none', borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 600, cursor: 'pointer', marginTop: '4px' },

  // Medication card
  medCard:    { background: 'var(--surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', overflow: 'hidden' },
  medCardTop: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px 16px 12px', gap: '10px' },
  medInfo:    { flex: 1 },
  medName:    { fontSize: '15px', fontWeight: 700, color: 'var(--ink)', lineHeight: 1.3 },
  medSub:     { fontSize: '12px', color: 'var(--ink-3)', marginTop: '3px' },
  deleteBtn:  { color: 'var(--ink-4)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px', flexShrink: 0, lineHeight: 1 },
  deleteConfirm:    { display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 },
  deleteConfirmText:{ fontSize: '12px', color: 'var(--ink-3)' },
  deleteYes:  { padding: '4px 10px', background: '#fef3f0', color: '#c0392b', border: '1px solid rgba(192,57,43,0.2)', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 600, cursor: 'pointer' },
  deleteNo:   { padding: '4px 10px', background: 'var(--cream)', color: 'var(--ink-3)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 600, cursor: 'pointer' },

  // Rx cost comparison row
  rxRow:    { display: 'flex', alignItems: 'center', borderTop: '1px solid var(--border)', padding: '12px 16px', gap: '8px' },
  rxCol:    { flex: 1 },
  rxLabel:  { fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-4)', marginBottom: '4px' },
  rxValue:  { fontSize: '14px', fontWeight: 700, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' },
  rxLink:   { fontSize: '12px', fontWeight: 600, color: 'var(--amber)', textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', padding: 0 },
  rxDivider:{ fontSize: '11px', fontWeight: 600, color: 'var(--ink-4)', flexShrink: 0, padding: '0 4px' },
  saveBadge:{ fontSize: '9px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', background: 'var(--teal-faint)', color: 'var(--teal)', padding: '2px 6px', borderRadius: '100px', border: '1px solid rgba(30,92,84,0.15)' },

  // Savings callout
  savingsCallout:{ display: 'flex', alignItems: 'flex-start', gap: '7px', padding: '10px 16px', background: 'var(--teal-faint)', borderTop: '1px solid rgba(30,92,84,0.12)', fontSize: '11.5px', color: 'var(--teal)', lineHeight: 1.4, fontWeight: 500 },
  savingsLink:   { display: 'block', fontWeight: 700, color: 'var(--teal)', textDecoration: 'none', marginTop: '3px' },

  // Add form
  addCard:      { background: 'var(--surface)', borderRadius: 'var(--radius-lg)', border: '1.5px solid var(--teal-light)', padding: '18px' },
  addTitle:     { fontSize: '14px', fontWeight: 700, color: 'var(--ink)', marginBottom: '14px' },
  addFieldGroup:{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px', position: 'relative' },
  addFieldLabel:{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-3)', display: 'flex', alignItems: 'center', gap: '6px' },
  optional:     { fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: '11px', color: 'var(--ink-4)' },
  addInput:     { width: '100%', padding: '11px 14px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--border-strong)', background: 'var(--cream)', fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--ink)', outline: 'none', boxSizing: 'border-box' },
  addRow:       { display: 'flex', gap: '10px', marginBottom: '14px' },
  addActions:   { display: 'flex', gap: '10px' },
  cancelBtn:    { flex: 1, padding: '12px', background: 'var(--cream)', color: 'var(--ink-3)', border: '1.5px solid var(--border-strong)', borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 600, cursor: 'pointer' },
  addBtn:       { flex: 2, padding: '12px', background: 'var(--teal)', color: 'white', border: 'none', borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 600, cursor: 'pointer' },

  // Autocomplete suggestions
  suggestions:    { position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--surface)', border: '1.5px solid var(--border-strong)', borderRadius: 'var(--radius-md)', overflow: 'hidden', zIndex: 10, boxShadow: 'var(--shadow-md)', marginTop: '2px' },
  suggestionRow:  { width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '10px 14px', background: 'none', border: 'none', borderBottom: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'var(--font-body)', textAlign: 'left' },
  suggestionName: { fontSize: '14px', fontWeight: 600, color: 'var(--ink)' },
  suggestionMeta: { fontSize: '11px', color: 'var(--ink-4)', marginTop: '2px' },

  // Add more button
  addMoreBtn: { width: '100%', padding: '14px', background: 'var(--surface)', border: '1.5px dashed var(--border-strong)', borderRadius: 'var(--radius-lg)', fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 600, color: 'var(--teal)', cursor: 'pointer' },
};
